import {
  Component,
  OnInit,
  ViewChild,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'

import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import {
  CompanyProductFilterResult,
  ProductData,
  ProductFilter,
} from '../../../types/products'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatFormFieldModule } from '@angular/material/form-field'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatInputModule } from '@angular/material/input'
import { MatButtonModule } from '@angular/material/button'
import { MatMenuModule } from '@angular/material/menu'
import { RouteService } from '../../../services/route.service'
import { AuthService } from '../../../services/auth.service'
import { ROUTES } from '../../../constants/routes'
import { MatIconModule } from '@angular/material/icon'
import { ProductListComponent } from '../../product-list/product-list.component'
import { firstValueFrom } from 'rxjs/internal/firstValueFrom'
import { ApiError } from '../../../types/apiError'
import { Dialog } from '@angular/cdk/dialog'
import { MatDialog } from '@angular/material/dialog'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { DialogService } from '../../../services/dialog.service'
import { ErrorService } from '../../../services/error.service'
import { ProductService } from '../../../services/product.service'
import { SpinnerService } from '../../../services/spinner.service'
import { WindowService } from '../../../services/window.service'
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator'
import { USER_ROLES } from '../../../constants/auth'
import { PaginatorIntl } from '../../intl/paginator-intl'
import { TranslationService } from '../../../services/translation.service'
import { ProductFilterComponent } from '../../product-filter/product-filter.component'
import { DiscontinuedYearDialogComponent } from '../../shared/discontinued-year-dialog/discontinued-year-dialog.component'
@Component({
  selector: 'app-company-products',
  imports: [
    ProductListComponent,
    MatTableModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatPaginatorModule,
    RouterModule,
    ProductFilterComponent,
  ],
  providers: [
    {
      provide: MatPaginatorIntl,
      useClass: PaginatorIntl,
    },
  ],
  templateUrl: './company-products.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './company-products.component.scss',
})
export class CompanyProductsComponent implements OnInit {
  route = inject(ActivatedRoute)
  dialog = inject(Dialog)
  matDialog = inject(MatDialog)
  dialogService = inject(DialogService)
  routeService = inject(RouteService)
  productService = inject(ProductService)
  spinnerService = inject(SpinnerService)
  errorService = inject(ErrorService)
  windowService = inject(WindowService)
  authService = inject(AuthService)
  translationService = inject(TranslationService)
  router = inject(Router)

  @ViewChild(MatPaginator) paginator!: MatPaginator

  public companyProductResults?: CompanyProductFilterResult
  public displayedColumns: Array<string> = [
    'name',
    'brand',
    'marketplaceCategory',
    'viewAction',
    'toggleDiscontinuedSlider',
  ]
  canExportProducts = false
  exportingProducts = false
  isMaster: boolean = false
  viewedCompanyId: string | null = null

  productFilter: ProductFilter = {
    page: 1,
    name: null,
    brand: null,
    sku: null,
    batch: null,
    serial: null,
    gs1Identifier: null,
  }

  dataSource = new MatTableDataSource<ProductData>(
    this.companyProductResults?.results,
  )

  async ngOnInit(): Promise<void> {
    this.viewedCompanyId = this.route.snapshot.queryParamMap.get('companyId')
    const currentRoute = this.routeService.getUserRoutes(
      ROUTES.COMPANY_PRODUCTS.title,
      `/admin/companies/${ROUTES.COMPANY_PRODUCTS.path}`,
    )

    const session = this.authService.getSessionFromLS()

    if (session) {
      this.isMaster = session.user.role === USER_ROLES.MASTER
      this.canExportProducts =
        session.user.role === USER_ROLES.ADMIN ||
        session.user.role === USER_ROLES.COMPLIANCE_OFFICER
    }
    if (this.isMaster) {
      this.displayedColumns = [
        'name',
        'brand',
        'marketplaceCategory',
        'viewAction',
      ]
    }

    this.routeService.setRoute(currentRoute)
    await this.getCompanyProducts()
  }

  /**
   * Handles the page change events from the paginator and gets the new page data from the API
   * @param {PageEvent} event Event containing new page
   */
  changePage(event: PageEvent) {
    this.productFilter.page = event.pageIndex + 1
    this.getCompanyProducts()
  }

  /**
   * Makes request to get company products
   */
  async getCompanyProducts() {
    try {
      this.spinnerService.startSpinner()

      this.companyProductResults = await firstValueFrom(
        this.productService.getCompanyProducts(
          this.productFilter,
          this.viewedCompanyId,
        ),
      )

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  async exportProducts(): Promise<void> {
    if (!this.canExportProducts || this.exportingProducts) return
    this.exportingProducts = true
    this.spinnerService.startSpinner()
    try {
      const response = await firstValueFrom(
        this.productService.exportCompanyProducts(),
      )
      const blob = response.body
      if (
        !blob ||
        blob.size === 0 ||
        blob.type.split(';')[0] !== 'application/zip'
      ) {
        throw new Error('Invalid product export response')
      }
      const disposition = response.headers.get('Content-Disposition') ?? ''
      const encodedFilename = /filename\*=utf-8''([^;]+)/i.exec(
        disposition,
      )?.[1]
      const filename = encodedFilename
        ? decodeURIComponent(encodedFilename)
        : /filename="?([^";]+)"?/i.exec(disposition)?.[1]
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      try {
        link.href = url
        link.download = filename ?? 'products.zip'
        link.hidden = true
        document.body.appendChild(link)
        link.click()
      } finally {
        link.remove()
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      }
    } catch {
      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getErrorText(),
        this.translationService.getProductExportFailedText(),
      )
    } finally {
      this.exportingProducts = false
      this.spinnerService.stopSpinner()
    }
  }

  /**
   * Routes user to Product Creation screen
   */
  goToCreate() {
    this.router.navigate(['/product-add'])
  }

  /**
   * Routes user to Product Import screen
   */
  goToImport() {
    this.router.navigate(['/import-products'])
  }

  /**
   * Filters products and resets the selected page
   */
  async filter(filter: ProductFilter) {
    try {
      this.spinnerService.startSpinner()
      this.productFilter = {
        ...filter,
        page: 1,
      }

      if (this.paginator != null) {
        //May not be available instantly (eg in ngOnInit), so update only when available
        this.paginator.pageIndex = 0
      }

      this.companyProductResults = await firstValueFrom(
        this.productService.getCompanyProducts(
          this.productFilter,
          this.viewedCompanyId,
        ),
      )

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Routes user to Add to Catalogue Screen
   */
  goToAddToCatalogue() {
    this.router.navigate(['/add-to-catalogue'])
  }

  /**
   * Handles product discontinued toggle events from the product list.
   * Keeps the UI and product state in sync, prompts for a discontinued year
   * when needed, and reverts on cancel or backend failure.
   */
  async onToggleDiscontinue(event: {
    productId: string
    discontinued: boolean
  }) {
    const { productId, discontinued } = event
    const product = this.companyProductResults?.results.find(
      (p) => p.id === productId,
    )

    if (!product) {
      return
    }

    const previousDiscontinued = !!product.discontinued
    const previousYear = product.discontinuedYear ?? null

    // Keep the toggle and product state in sync immediately
    product.discontinued = discontinued

    let discontinuedYear: number | undefined

    // If the product is being discontinued, prompt for the year
    if (discontinued) {
      const dialogRef = this.matDialog.open(DiscontinuedYearDialogComponent, {
        data: {
          defaultYear: previousYear ?? new Date().getFullYear(),
        },
      })

      const selectedYear = await firstValueFrom(dialogRef.afterClosed())

      // If user cancels the dialog, revert the state and do nothing
      if (selectedYear == null) {
        product.discontinued = previousDiscontinued
        product.discontinuedYear = previousYear
        return
      }

      discontinuedYear = selectedYear
      product.discontinuedYear = selectedYear
    } else {
      // Re-activating the product clears the discontinued year locally
      product.discontinuedYear = null
    }

    try {
      this.spinnerService.startSpinner()

      await firstValueFrom(
        this.productService.updateDiscontinuedStatus(
          productId,
          discontinued,
          discontinuedYear,
        ),
      )

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()

      // Revert to the previous state on error
      product.discontinued = previousDiscontinued
      product.discontinuedYear = previousYear

      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Routes user to Bulk Product Sharing Screen
   */
  goToBulkProductSharing() {
    this.router.navigate([`/${ROUTES.IMPORT_PRODUCT_SHARING.path}`])
  }
}
